import { isCollectionBlock } from '@/lib/blocks/collection/blocks';
import { BLOCK_STACK_DIRECTION_DEFAULT } from '@/lib/blocks/layout/block-stack-layout';
import type { GroupDirection } from '@/lib/blocks/layout/group-layout';
import { resolveGroupLayout } from '@/lib/blocks/layout/group-layout';
import { readRecord } from '@/lib/prop-utils';
import type { PageBlock } from '@/types/block';
import type { DropIndicator, DropValidationEventInput, GapZoneData } from '../core';
import {
  getEventActivator,
  getEventOver,
  isDirectionalEdge,
  isGapZoneData,
  isPointerDrivenActivator,
  resolveCanonicalDropIndicator,
} from '../core';
import { getEventActiveTranslatedRect } from '../resolution/event-adapters';

const DROP_EDGE_TUNING = {
  // How close pointer must be to an edge to activate it.
  edgeActivationRatio: 0.32,
  // Neutral center area where we avoid aggressive edge switching.
  centerDeadZoneRatio: 0.22,
  // Sticky margin for keeping previously selected edge during minor jitter.
  edgeHysteresisRatio: 0.16,
  // Slight directional preference when distances are very close.
  directionBiasRatio: 0.04,
  // Side bands used for left/right prioritization logic.
  crossAxisSideBandRatio: 0.22,
  // Vertical parents: top area has stronger top-edge priority.
  verticalTopPriorityBandRatio: 0.2,
  // Horizontal parents: top/bottom bands get stronger priority.
  horizontalTopBottomPriorityBandRatio: 0.45,
} as const;

// Math helpers for normalized edge-distance comparisons.
const clamp01 = (value: number): number => Math.max(0, Math.min(1, value));

const getDragPoint = (event: DropValidationEventInput): { x: number; y: number } | null => {
  const translatedRect = getEventActiveTranslatedRect(event);
  if (translatedRect) {
    const basePoint = {
      x: translatedRect.left + translatedRect.width / 2,
      y: translatedRect.top + translatedRect.height / 2,
    };
    if ('operation' in event) {
      const activator = getEventActivator(event);
      const maybeClientX =
        activator && 'clientX' in activator ? (activator as { clientX: unknown }).clientX : null;
      const maybeClientY =
        activator && 'clientY' in activator ? (activator as { clientY: unknown }).clientY : null;

      if (typeof maybeClientX === 'number' && typeof maybeClientY === 'number') {
        return { x: maybeClientX, y: maybeClientY };
      }

      if (typeof maybeClientX === 'number') {
        return { ...basePoint, x: maybeClientX };
      }
    }

    return basePoint;
  }

  const overRect = getEventOver(event)?.rect;
  if (overRect) {
    return {
      x: overRect.left + overRect.width / 2,
      y: overRect.top + overRect.height / 2,
    };
  }

  return null;
};

type IndicatorEdge = DropIndicator['edge'];
type DropSurfaceKind =
  | 'none'
  | 'insert-gap-zone'
  | 'group-gap-zone'
  | 'parent-drop'
  | 'block'
  | 'unknown';

export interface DropOperation {
  dropSurface: DropSurfaceKind;
  indicator: DropIndicator | null;
}

const resolveDropSurfaceKind = (data: unknown): DropSurfaceKind => {
  const type = readRecord(data)?.type;
  if (type === 'insert-gap-zone' || type === 'group-gap-zone' || type === 'parent-drop') {
    return type;
  }
  if (type === 'block') {
    return 'block';
  }
  return 'unknown';
};

const getDistanceForEdge = ({
  edge,
  distanceTop,
  distanceBottom,
  distanceLeft,
  distanceRight,
}: {
  edge: GapZoneData['edge'];
  distanceTop: number;
  distanceBottom: number;
  distanceLeft: number;
  distanceRight: number;
}): number => {
  switch (edge) {
    case 'top':
      return distanceTop;
    case 'bottom':
      return distanceBottom;
    case 'left':
      return distanceLeft;
    case 'right':
      return distanceRight;
  }
};

const resolveFallbackEdgeFromBlockSurface = ({
  event,
  overData,
  previousIndicator,
}: {
  event: DropValidationEventInput;
  overData: GapZoneData;
  previousIndicator: DropIndicator | null;
}): IndicatorEdge => {
  const overRect = getEventOver(event)?.rect;
  const point = getDragPoint(event);
  if (!overRect || !point) {
    return overData.edge;
  }

  const localX = clamp01((point.x - overRect.left) / overRect.width);
  const localY = clamp01((point.y - overRect.top) / overRect.height);
  const distanceTop = localY;
  const distanceBottom = 1 - localY;
  const distanceLeft = localX;
  const distanceRight = 1 - localX;
  const verticalDistance = Math.min(distanceTop, distanceBottom);
  const horizontalDistance = Math.min(distanceLeft, distanceRight);

  const isCenterDeadZone =
    Math.abs(localX - 0.5) < DROP_EDGE_TUNING.centerDeadZoneRatio &&
    Math.abs(localY - 0.5) < DROP_EDGE_TUNING.centerDeadZoneRatio;
  const isHorizontalParent = overData.parentDirection === 'horizontal';
  const isVerticalParent = overData.parentDirection === 'vertical';
  const isGridParent = overData.parentDirection === 'grid';
  const inLeftSideBand = localX <= DROP_EDGE_TUNING.crossAxisSideBandRatio;
  const inRightSideBand = localX >= 1 - DROP_EDGE_TUNING.crossAxisSideBandRatio;
  const sameTargetPrevious =
    previousIndicator?.targetId === overData.blockId ? previousIndicator.edge : null;

  if (isVerticalParent && localY <= DROP_EDGE_TUNING.verticalTopPriorityBandRatio) {
    return 'top';
  }

  if (isGridParent) {
    if (inLeftSideBand) {
      return 'left';
    }
    if (inRightSideBand) {
      return 'right';
    }
    if (sameTargetPrevious === 'left' || sameTargetPrevious === 'right') {
      return sameTargetPrevious;
    }
    return localX <= 0.5 ? 'left' : 'right';
  }

  if (isHorizontalParent) {
    if (localY <= DROP_EDGE_TUNING.horizontalTopBottomPriorityBandRatio) {
      return 'top';
    }
    if (localY >= 1 - DROP_EDGE_TUNING.horizontalTopBottomPriorityBandRatio) {
      return 'bottom';
    }
  }

  if (isVerticalParent && (inLeftSideBand || inRightSideBand)) {
    return inLeftSideBand ? 'left' : 'right';
  }

  const rawAxisDelta = horizontalDistance - verticalDistance;
  const axisDelta =
    rawAxisDelta +
    (isHorizontalParent
      ? -DROP_EDGE_TUNING.directionBiasRatio
      : isVerticalParent
        ? DROP_EDGE_TUNING.directionBiasRatio
        : 0);
  const useHorizontalAxis = axisDelta < 0;
  const candidateEdge: IndicatorEdge = useHorizontalAxis
    ? distanceLeft <= distanceRight
      ? 'left'
      : 'right'
    : distanceTop <= distanceBottom
      ? 'top'
      : 'bottom';

  if (isCenterDeadZone && isDirectionalEdge(sameTargetPrevious)) {
    return sameTargetPrevious;
  }

  const candidateDistance = getDistanceForEdge({
    edge: candidateEdge,
    distanceTop,
    distanceBottom,
    distanceLeft,
    distanceRight,
  });
  if (candidateDistance <= DROP_EDGE_TUNING.edgeActivationRatio) {
    return candidateEdge;
  }

  if (isDirectionalEdge(sameTargetPrevious)) {
    const previousDistance = getDistanceForEdge({
      edge: sameTargetPrevious,
      distanceTop,
      distanceBottom,
      distanceLeft,
      distanceRight,
    });
    if (
      previousDistance <=
      DROP_EDGE_TUNING.edgeActivationRatio + DROP_EDGE_TUNING.edgeHysteresisRatio
    ) {
      return sameTargetPrevious;
    }
  }

  return candidateEdge;
};

const getBlockParentDirection = ({
  overBlock,
  blockMap,
  stableIdByPersistedId,
  rootParentDirection,
}: {
  overBlock: PageBlock;
  blockMap: Map<string, PageBlock>;
  stableIdByPersistedId: Map<string, string>;
  rootParentDirection?: GroupDirection;
}): GapZoneData['parentDirection'] => {
  const defaultDirection = rootParentDirection ?? BLOCK_STACK_DIRECTION_DEFAULT;
  if (!overBlock.parentBlockId) {
    return defaultDirection;
  }

  const parentStableId =
    stableIdByPersistedId.get(overBlock.parentBlockId) ?? overBlock.parentBlockId;
  const parentBlock = blockMap.get(parentStableId);
  if (!parentBlock) {
    return defaultDirection;
  }
  if (isCollectionBlock(parentBlock)) {
    return 'grid';
  }
  if (parentBlock.blockType === 'group') {
    const children = Array.from(blockMap.values()).filter(
      (block) => (block.parentBlockId ?? null) === parentBlock.id,
    );
    const layout = resolveGroupLayout(parentBlock, children);
    return layout.isFloatLayout ? 'vertical' : layout.direction;
  }
  return defaultDirection;
};

export const resolveDropOperation = ({
  event,
  blocks,
  blockMap,
  stableIdByPersistedId,
  previousIndicator,
  rootParentDirection,
}: {
  event: DropValidationEventInput;
  blocks: PageBlock[];
  blockMap: Map<string, PageBlock>;
  stableIdByPersistedId: Map<string, string>;
  previousIndicator: DropIndicator | null;
  rootParentDirection?: GroupDirection;
}): DropOperation => {
  const dropSurface = getEventOver(event);
  if (!dropSurface) {
    return { dropSurface: 'none', indicator: null };
  }

  const dropSurfaceData = dropSurface.data.current;
  const dropSurfaceType = readRecord(dropSurfaceData)?.type;
  const dropSurfaceKind = resolveDropSurfaceKind(dropSurfaceData);

  let indicator = resolveCanonicalDropIndicator({
    event,
    over: dropSurface,
    blockMap,
    stableIdByPersistedId,
    allBlocks: blocks,
  });

  if (isGapZoneData(dropSurfaceData)) {
    indicator = {
      targetId: dropSurfaceData.blockId,
      edge: dropSurfaceData.edge,
    };
  }

  // Explicit gap zones are the primary source of pointer intent.
  // We only derive from block bodies for non-pointer fallbacks (e.g. keyboard).
  const activator = getEventActivator(event);
  const isPointerDriven = isPointerDrivenActivator(activator);
  if (dropSurfaceType === 'block' && !indicator && !isPointerDriven) {
    const overBlock = blockMap.get(String(dropSurface.id));
    if (overBlock) {
      indicator = {
        targetId: String(dropSurface.id),
        edge: resolveFallbackEdgeFromBlockSurface({
          event,
          overData: {
            // Synthetic insert lane used for non-pointer fallback derivation.
            type: 'insert-gap-zone',
            blockId: String(dropSurface.id),
            edge: 'top',
            parentId: overBlock.parentBlockId
              ? (stableIdByPersistedId.get(overBlock.parentBlockId) ?? overBlock.parentBlockId)
              : null,
            parentDirection: getBlockParentDirection({
              overBlock,
              blockMap,
              stableIdByPersistedId,
              rootParentDirection,
            }),
          },
          previousIndicator,
        }),
      };
    }
  }

  return {
    dropSurface: dropSurfaceKind,
    indicator,
  };
};
